import React, { FC, useEffect } from 'react';
import cn from 'classnames';

import { ILibModalSizes, Modal } from '@infologistics/frontend-libraries';

import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { resetModal } from '@store/modules/modal';

import { IModalProps as IProps } from '@common/ModalWrapper/types';
import { useTranslation } from 'react-i18next';

const SCROLL_SHADOW_OFFSET = 1;

const ModalWrapper: FC<IProps> = (props) => {
  const {
    children,
    size = ILibModalSizes.SMALL,
    disableHideFn,
    portalClasses
  } = props

  const { t } = useTranslation(['common'])

  const { modal } = useAppSelector(state => state.modal)

  const dispatch = useAppDispatch()

  const handleModalHide = disableHideFn
    ? () => null
    : () => (dispatch(resetModal()))

  useEffect(() => {
    if (!modal) return undefined;

    let frameId: number | null = null;
    let resizeObserver: ResizeObserver | null = null;
    const modalBodies: HTMLElement[] = [];

    const updateModalScrollShadows = (): void => {
      document
        .querySelectorAll<HTMLElement>('.fl-portal.app-modal-portal .fl-modal_content')
        .forEach((content) => {
          const body = content.querySelector<HTMLElement>('.modal-body');

          if (!body) return;

          const hasTopOverflow = body.scrollTop > SCROLL_SHADOW_OFFSET;
          const hasBottomOverflow =
            body.scrollTop + body.clientHeight < body.scrollHeight - SCROLL_SHADOW_OFFSET;

          content.classList.toggle('app-modal-content_has-top-shadow', hasTopOverflow);
          content.classList.toggle('app-modal-content_has-bottom-shadow', hasBottomOverflow);
        });
    };

    const scheduleUpdate = (): void => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);

      frameId = window.requestAnimationFrame(updateModalScrollShadows);
    };

    const attachScrollListeners = (): void => {
      document
        .querySelectorAll<HTMLElement>('.fl-portal.app-modal-portal .modal-body')
        .forEach((body) => {
          body.scrollTop = 0;
          modalBodies.push(body);
          body.addEventListener('scroll', scheduleUpdate);
          resizeObserver?.observe(body);
        });

      scheduleUpdate();
    };

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleUpdate);
    }

    const timeoutId = window.setTimeout(attachScrollListeners, 0);
    document.documentElement.classList.add('app-modal-open');
    document.body.classList.add('app-modal-open');
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', scheduleUpdate);
      document.documentElement.classList.remove('app-modal-open');
      document.body.classList.remove('app-modal-open');
      modalBodies.forEach((body) => body.removeEventListener('scroll', scheduleUpdate));
      resizeObserver?.disconnect();

      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [modal]);

  return (
    <Modal
      {...props}
      idFromState={modal || ''}
      portalClasses={cn('app-modal-portal', portalClasses)}
      size={size}
      closeText={t('common:close')}
      hideModal={handleModalHide}
    >
      { children }
    </Modal>
  );
}

export default ModalWrapper
